import { GenericModal } from "components/Modals/GenericModal";
import { Asset } from "types/asset";
import { icons } from 'assets/icons/index'
import { Radio } from "components/inputs/Radio/Radio";
import { useAssetListStore } from "contexts/RootStore.context";
import { useEffect, useMemo, useState } from "react";
import { Vertical } from "gls";
import { style } from "typestyle";
import { colours } from "styling/colours";
import DateTimePicker from "components/Dates/DateTimePicker";
import { isEmpty } from "lodash";
import { add, addHours } from "date-fns";
import { UpdateAssetStatus } from "api/assets.api";
import { alertErrorMessage, alertSuccessMessage } from "utils/alerts";


type ModalProps = {
  onClose: () => void;
  onUpdate: () => void;
  asset: Asset
};

export const UpdateStatusModal = (props: ModalProps) => {

  const { assetStatuses, clearPeriods } = useAssetListStore()
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [customDatetime, setCustomDatetime] = useState('')
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)

  const statusOptions = useMemo(() => {
    const list = assetStatuses.map(r => ({ key: r.description, value: r.asset_status_id }))
    if (!isEmpty(props.asset.status_assignment?.asset_status))
      list.push({
        key: "Clear Status", value: 'clear'
      })
    return list
  }, [assetStatuses, props.asset.status_assignment?.asset_status])

  useEffect(() => {
    if (selectedStatus === "" && statusOptions.length > 0)
      setSelectedStatus(statusOptions[0].value)
  }, [selectedStatus, statusOptions])

  useEffect(() => {
    if (selectedPeriod === "") {
      setSelectedPeriod(clearPeriods.length > 0
        ? clearPeriods[0].minutes.toString()
        : "custom"
      )
    }
  }, [clearPeriods, selectedPeriod])

  function getUnitLabel(minutes: number) {
    if (minutes === 1)
      return `${minutes} min`;
    if (minutes < 60)
      return `${minutes} mins`;
    if (minutes === 60)
      return `1 hour`;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const hasMins = mins > 0;
    const isSingleHour = hours === 1;
    const isSingleMinute = mins === 1;

    return `${hours} ${isSingleHour ? 'hour' : 'hours'} ${hasMins ? `${mins} ${isSingleMinute ? 'min' : 'mins'} ` : ''}`;
  }

  const onAction = () => {
    if (step === 1) {
      if (selectedStatus === 'clear')
        onUpdate(selectedStatus, selectedPeriod, customDatetime)

      else
        setStep(2)
    }
    else
      onUpdate(selectedStatus, selectedPeriod, customDatetime)
  }

  const onUpdate = (selectedStatus: string, selectedPeriod: string, customDatetime: string) => {
    if (!props.asset)
      return;

    const asset_status_id = selectedStatus === "clear" ? null : selectedStatus
    let clear_at_timestamp = null;
    if (selectedStatus !== "clear" && selectedPeriod !== "noClear")
      if (selectedPeriod === "custom")
        clear_at_timestamp = customDatetime
      else {
        const derivedDate = add(new Date(), { minutes: parseInt(selectedPeriod, 10) });
        clear_at_timestamp = derivedDate.toISOString();
      }

    setLoading(true);
    UpdateAssetStatus(props.asset.asset_id, asset_status_id, clear_at_timestamp)
      .then(() => {
        setLoading(false);
        props.onUpdate()
        alertSuccessMessage(`${props.asset.asset_name} - Status has been updated successfully`);
      })

      .catch((err) => {
        setLoading(false);
        console.log(err);
        alertErrorMessage(`${props.asset.asset_name} - Status could not be updated, please try again in a few moments `);
      });
  }

  return (
    <GenericModal isOpen onClose={props.onClose}
      title={step === 1 ? "Update Asset Status" : "Clear Status"}
      roundIconStyle={false}
      icon={step === 1
        ? <icons.UpdateIcon aria-label="Update Status" fill={colours.secondary} />
        : <icons.RemoveIcon aria-label="Clear Status" width='40px' height='40px' fill={colours.secondaryText} />}
      actionName={step === 1 ? 'Confirm' : 'Set'}
      actionDisabled={step === 1 ? isEmpty(selectedStatus) : isEmpty(selectedPeriod)}
      onAction={onAction}
      loading={loading}
    >
      {step === 1 && (
        <Vertical spacing={10}>
          <span className={style({ color: colours.secondaryText })}>Update this assets status to:</span>
          <Radio
            name="asset-statuses"
            options={statusOptions}
            value={selectedStatus}
            onChange={setSelectedStatus}
            direction="vertical"
          />
        </Vertical>
      )}
      {step === 2 && (
        < Vertical spacing={10}>
          <span className={style({ color: colours.secondaryText })}>
            Clear "{assetStatuses.find(r => r.asset_status_id === selectedStatus)?.description}" status after:</span>
          <Radio
            name='clear-periods'
            options={clearPeriods.map(r => ({ value: r.minutes.toString(), key: getUnitLabel(r.minutes) }))}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            direction="vertical"
          />
          <Radio
            name='clear-periods'
            options={[{ key: "Custom", value: "custom" }]}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            direction="vertical"
          />
          {selectedPeriod === "custom" && (
            <DateTimePicker
              id="custom-date"
              defaultValue={addHours(new Date(), 1)}
              onChange={setCustomDatetime}
              minDate={new Date()} />
          )}
          <Radio
            name='clear-periods'
            options={[{ key: "Do not clear", value: "noClear" }]}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            direction="vertical"
          />
        </Vertical>
      )}
    </GenericModal >
  );
};